import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services';
import { ChevronLeft } from 'lucide-react';
import Input from '../../components/ui/Input';
import { LoadingSpinner } from '../../components/ui/Loading';
import CreateItemModal from '../../components/modals/CreateItemModal';
import { 
  getItemTypeTranslation, 
  getTypeIcon 
} from '../../utils/itemTranslations';

const AdminItems = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState('');
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    type: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.type) params.type = filters.type;

      const response = await adminService.getItems(params);
      
      // Verificação de segurança para garantir que items seja sempre um array
      const itemsData = response?.data?.items || [];
      const totalData = response?.data?.total || 0;
      const totalPagesData = response?.data?.totalPages || 0;
      
      setItems(itemsData);
      setPagination(prev => ({
        ...prev,
        total: totalData,
        totalPages: totalPagesData
      }));
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
      setError('Erro ao carregar itens. Tente novamente.');
      setItems([]); // Garantir que items seja sempre um array vazio em caso de erro
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCreateItem = async (itemData) => {
    try {
      setError('');
      await adminService.createItem(itemData);
      setShowCreateModal(false);
      fetchItems();
    } catch (error) {
      console.error('Erro ao criar item:', error);
      throw error;
    }
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-8 border border-red-500/20 shadow-xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-5xl">🚫</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Acesso Negado
          </h2>
          <p className="text-gray-400 mb-6">
            Apenas administradores podem acessar esta página.
          </p>
          <button
            onClick={() => window.location.href = '/app/dashboard'}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.location.href = '/app/admin'}
                className="flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-1">
                  📦 Gerenciar Itens
                </h1>
                <p className="text-gray-300">Administre todos os itens do sistema</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20 whitespace-nowrap"
            >
              + Criar Novo Item
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-gradient-to-br from-red-900/20 to-red-800/20 backdrop-blur-xl rounded-2xl p-4 border border-red-500/20 shadow-xl">
            <p className="text-red-400 text-center">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                🔍 Buscar
              </label>
              <Input
                placeholder="Nome do item..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                🏷️ Tipo
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 hover:border-purple-500/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-300"
              >
                <option value="" className="bg-slate-800">Todos os tipos</option>
                <option value="WEAPON" className="bg-slate-800">⚔️ Armas</option>
                <option value="ARMOR" className="bg-slate-800">🛡️ Armaduras</option>
                <option value="ROBE" className="bg-slate-800">👘 Roupões</option>
                <option value="BOOTS" className="bg-slate-800">👢 Botas</option>
                <option value="GLOVES" className="bg-slate-800">🧤 Luvas</option>
                <option value="BRACELET" className="bg-slate-800">📿 Braceletes</option>
                <option value="SHIELD" className="bg-slate-800">🛡️ Escudos</option>
                <option value="ACCESSORY" className="bg-slate-800">💎 Acessórios</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
              >
                🔄 Limpar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Items Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : !items || items.length === 0 ? (
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-12 border border-white/10 shadow-xl text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Nenhum item encontrado
            </h3>
            <p className="text-gray-400 mb-6">
              {filters.search || filters.type 
                ? 'Tente ajustar os filtros de busca.'
                : 'Não há itens cadastrados no sistema ainda.'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20"
            >
              + Criar Primeiro Item
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {(items || []).map((item) => (
              <div 
                key={item.id}
                className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl group"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-4xl group-hover:scale-110 transition-transform duration-300">
                    {getTypeIcon(item.type)}
                  </span>
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs font-semibold rounded-lg border border-purple-500/30">
                    {getItemTypeTranslation(item.type)}
                  </span>
                </div>
                
                <h3 className="font-bold text-white text-lg mb-2 group-hover:text-purple-400 transition-colors">
                  {item.name}
                </h3>
                <p className="text-sm text-gray-400 mb-4 line-clamp-2 min-h-[2.5rem]">
                  {item.description}
                </p>
                
                <div className="space-y-2 pt-4 border-t border-white/10">
                  {item.attackPower && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400 flex items-center gap-1">
                        ⚔️ Ataque:
                      </span>
                      <span className="font-bold text-red-400 bg-red-500/10 px-2 py-1 rounded">
                        {item.attackPower}
                      </span>
                    </div>
                  )}
                  {item.defensePower && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400 flex items-center gap-1">
                        🛡️ Defesa:
                      </span>
                      <span className="font-bold text-blue-400 bg-blue-500/10 px-2 py-1 rounded">
                        {item.defensePower}
                      </span>
                    </div>
                  )}
                  {!item.attackPower && !item.defensePower && (
                    <div className="text-center text-gray-500 text-xs italic py-2">
                      Sem atributos especiais
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-4 border border-white/10 shadow-xl">
            <div className="flex justify-center items-center gap-3">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
              >
                ← Anterior
              </button>

              <span className="px-6 py-2 text-sm text-gray-300 bg-white/5 rounded-lg border border-white/10">
                Página <span className="font-bold text-white">{pagination.page}</span> de <span className="font-bold text-white">{pagination.totalPages}</span>
                <span className="text-gray-500 ml-2">({pagination.total} itens)</span>
              </span>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
              >
                Próxima →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Criação */}
      <CreateItemModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateItem}
      />
    </div>
  );
};

export default AdminItems;